<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import WhatsAppCampaignDetails from './WhatsAppCampaignDetails.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  // NOTICE: KAPTION-INTEGRATION
  // Campaign stats from Marketing Analytics API
  stats: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete', 'archive', 'unarchive']);

const router = useRouter();
const route = useRoute();
const accountId = computed(() => route.params.accountId);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_ARCHIVED = 'archived';

const { formatMessage } = useMessageFormatter();

// NOTICE: KAPTION-INTEGRATION
const isArchived = computed(() => props.status === STATUS_ARCHIVED);

const isActive = computed(() => {
  if (isArchived.value) return false;
  return props.isLiveChatType
    ? props.isEnabled
    : props.status !== STATUS_COMPLETED;
});

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value && !isArchived.value,
  'text-n-slate-9': isArchived.value,
}));

const campaignStatus = computed(() => {
  // NOTICE: KAPTION-INTEGRATION
  if (isArchived.value) {
    return t('CAMPAIGN.ARCHIVE.ARCHIVED');
  }

  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

// NOTICE: KAPTION-INTEGRATION
// Check if this is a WhatsApp campaign
const isWhatsAppCampaign = computed(() => {
  return props.inbox?.channel_type === INBOX_TYPES.WHATSAPP;
});

// NOTICE: KAPTION-INTEGRATION
// Handle card click to navigate to report page
const handleCardClick = event => {
  // Don't navigate if clicking on buttons
  if (event?.target?.closest('button')) {
    return;
  }

  if (isWhatsAppCampaign.value && props.id) {
    router.push({
      name: 'campaigns_whatsapp_report',
      params: {
        accountId: accountId.value,
        campaignId: props.id,
      },
    });
  }
};

// NOTICE: KAPTION-INTEGRATION
// Handle analytics button click
const handleAnalyticsClick = () => {
  if (isWhatsAppCampaign.value && props.id) {
    router.push({
      name: 'campaigns_whatsapp_report',
      params: {
        accountId: accountId.value,
        campaignId: props.id,
      },
    });
  }
};
</script>

<template>
  <CardLayout
    layout="row"
    :class="[
      isWhatsAppCampaign && id
        ? 'cursor-pointer hover:bg-n-alpha-1 transition-colors'
        : '',
      isArchived ? 'opacity-60' : '',
    ]"
    @click="handleCardClick"
  >
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <WhatsAppCampaignDetails
          v-else-if="isWhatsAppCampaign"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
          :stats="stats"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div
      class="flex items-center justify-end gap-2"
      :class="isArchived ? 'w-28' : 'w-20'"
    >
      <!-- NOTICE: KAPTION-INTEGRATION -->
      <!-- Report button for WhatsApp campaigns -->
      <Button
        v-if="isWhatsAppCampaign && id"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-bar-chart-3"
        :title="t('CAMPAIGN.REPORT.VIEW_REPORT')"
        @click.stop="handleAnalyticsClick"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click.stop="emit('edit')"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Archive/Unarchive buttons -->
      <Button
        v-if="isArchived"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-archive-restore"
        :title="t('CAMPAIGN.ARCHIVE.UNARCHIVE')"
        @click.stop="emit('unarchive')"
      />
      <Button
        v-if="!isArchived"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-archive"
        :title="t('CAMPAIGN.ARCHIVE.ARCHIVE')"
        @click.stop="emit('archive')"
      />
      <Button
        v-if="isArchived"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click.stop="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
